import MojitoCore from 'mojito/core';
import { INITIAL_STATE } from './slice.js';
import ApplicationTypes from './types.js';

const reduxInstance = MojitoCore.Services.redux;
const STORE_KEY = ApplicationTypes.STORE_KEY;

/**
 * Application selectors.
 *
 * @module ApplicationSelectors
 * @name selectors
 * @memberof Mojito.Applications.Sports
 */

/**
 * Selects application state.
 *
 * @function selectState
 *
 * @param {object} [state] - Application state object. If not provided then state from global {@link Mojito.Core.Services.redux|redux store} will be returned.
 * @returns {Mojito.Applications.Sports.ApplicationState} Application state.
 * @memberof Mojito.Applications.Sports.selectors
 */
export const selectState = state => {
    // Should be no need to fallback to global state once fully migrated to Redux.
    const appState = state || reduxInstance.store?.getState();
    return appState?.[STORE_KEY] || INITIAL_STATE;
};

/**
 * Returns the current active sliding view that should be shown.
 *
 * @function selectActiveSlidingViewType
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Mojito.Applications.Sports.AppView.SLIDING_VIEW_TYPES|undefined} The current sliding view type or undefined if nothing is active.
 * @memberof Mojito.Applications.Sports.selectors
 */
export const selectActiveSlidingViewType = state => selectState(state).activeSlidingViewType;

/**
 * Returns the current active sliding data that can be used for particular view.
 *
 * @function selectActiveSlidingViewData
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {object} Sliding view data object.
 * @memberof Mojito.Applications.Sports.selectors
 */
export const selectActiveSlidingViewData = state => selectState(state).activeSlidingViewData;

/**
 * Returns if Mojito has been initialized or not.
 *
 * @function selectIsInitialized
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {boolean} If Mojito has been initialized or not.
 * @memberof Mojito.Applications.Sports.selectors
 */
export const selectIsInitialized = state => selectState(state).isInitialized;

/**
 * Returns flag indicating either betslip has been closed manually by user interaction.
 *
 * @function selectIsBetslipManuallyClosed
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {boolean} Either betslip has been closed manually.
 * @memberof Mojito.Applications.Sports.selectors
 */
export const selectIsBetslipManuallyClosed = state => selectState(state).isBetslipManuallyClosed;

/**
 * Subscribes to the changes of particular application state property.
 *
 * @function subscribe
 *
 * @type {Mojito.Core.Services.redux.SubscriberFunction}
 * @memberof Mojito.Applications.Sports.selectors
 */
export const subscribe = reduxInstance.getSubscriber(STORE_KEY);
